import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtMoneyK, useIsMobile } from './UI';

// Performance Targets widget. Shows the logged-in user's 6-tier annual
// ladder with realized $ progress, plus quarter + month pace tabs and the
// commission rate / earned summary at the bottom. Replaces the older
// single-quota widget.
//
// Tier names + ordering match the spreadsheet George provided:
//   Breakeven 5 / Bogey 10 / Par 15 / Birdie 20 / Eagle 25 / Hole in One 30
//
// Period denominator math:
//   Annual    = the tier value as-is
//   Quarter   = tier / 4 (the pace target for the current quarter)
//   Month     = tier / 12 (the pace target for the current month)

const TIERS = [
  { key: 'breakeven_value',   label: 'Breakeven 5' },
  { key: 'bogey_value',       label: 'Bogey 10' },
  { key: 'par_value',         label: 'Par 15' },
  { key: 'birdie_value',      label: 'Birdie 20' },
  { key: 'eagle_value',       label: 'Eagle 25' },
  { key: 'hole_in_one_value', label: 'Hole in One 30' }
];

// Tooltip text strings. Plain English, no jargon. Each one explains exactly
// where the number comes from so an AM can self-serve when asked "what
// counts toward this?"
const HELP = {
  widget:
    'Your 6 annual revenue goals, set by admin. The Annual / Quarter / Month ' +
    'buttons divide each goal by 1, 4, or 12 to show the pace target for the ' +
    'current period.',
  realized:
    'Your total billed $ from the monthly Sales Revenue upload (Rentalman) — ' +
    'every invoice credited to you, regardless of Loc. Combines Base ' +
    '(non-08xx Loc) + TA (08xx Loc) so you get credit for every dollar you ' +
    'sold no matter which branch booked it.',
  tier:
    'Each row is one of your revenue goals. The bar shows how close you are. ' +
    'Green check = you have already hit the pace for the current period.',
  commission:
    'Your commission rates: rentals × rentals % + labor × labor % + ' +
    'consumables × consumables %. Commission is paid on PAID invoices ' +
    'only (not just billed) — once a customer pays, the invoice flips ' +
    'from open to paid and the commission is earned.\n\n' +
    'Paid status comes from the RentalMan invoice report CRM loads ' +
    'several times a day, so Earned moves when a customer pays, not on ' +
    'an upload.'
};

const fmtPct = (v) => `${Math.min(999, Math.round(v * 100))}%`;

// Small (?) glyph with a native browser tooltip. Same visual style as the
// KPI tile help in Dashboard.jsx so users learn one pattern.
const HelpGlyph = ({ text }) => (
  <span
    title={text}
    aria-label="Help"
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 14, height: 14, borderRadius: '50%',
      border: '1px solid #9CA3AF', color: '#6B7280',
      fontSize: 9, fontWeight: 700, cursor: 'help', marginLeft: 6,
      verticalAlign: 'middle', flexShrink: 0
    }}
  >?</span>
);

export default function PerformanceTargetsWidget() {
  const [data, setData] = useState(null);
  const [comm, setComm] = useState(null); // commission summary from /commissions/me
  const [error, setError] = useState('');
  const isMobile = useIsMobile();
  // period is one of: 'annual' | 'q1'..'q4' | 'm1'..'m12' (Jan-Dec).
  // Defaults to 'annual'.
  const [period, setPeriod] = useState('annual');
  const year = new Date().getFullYear();

  useEffect(() => {
    api.myPerformanceTargets(year).then(setData).catch(e => setError(e.message));
    // Commission data comes from a separate endpoint that reads each
    // rentalman_invoices line's paid status from the RentalMan invoice feed
    // (rm_all_invoices). Returns has_paid_data = false until that feed has
    // loaded, in which case we keep the placeholder. Endpoint is forbidden
    // for ops users, so swallow the 403 silently.
    api.myCommissions(year).then(setComm).catch(() => setComm(null));
  }, [year]);

  // Pick which scope rows to display. Period can be:
  //   'annual'       -- whole year (denom 1)
  //   'q1'..'q4'     -- specific quarter (denom 4) -- sourced from data.quarters[]
  //   'm1'..'m12'    -- specific month (denom 12) -- sourced from data.months[]
  const scope = useMemo(() => {
    if (!data) return null;
    let src;
    let denom;
    let periodLabel;
    if (period === 'annual') {
      src = data.annual;
      denom = 1;
      periodLabel = `Annual ${year}`;
    } else if (/^q[1-4]$/.test(period)) {
      const qNum = Number(period[1]);
      const fromArray = data.quarters?.find(q => q.q === qNum);
      src = fromArray || data.quarter;
      denom = 4;
      periodLabel = `${src.period_label || `Q${qNum}`} ${year}`;
    } else if (/^m([1-9]|1[0-2])$/.test(period)) {
      const mNum = Number(period.slice(1));
      const fromArray = data.months?.find(m => m.m === mNum);
      src = fromArray || data.month;
      denom = 12;
      periodLabel = `${src.period_label || period} ${year}`;
    } else {
      src = data.annual;
      denom = 1;
      periodLabel = `Annual ${year}`;
    }
    return {
      denom,
      periodLabel,
      realized: src.realized,
      commission: src.commission_earned,
      breakdownCoverage: src.breakdown_coverage,
      billedCount: src.billed_project_count,
      breakdownCount: src.projects_with_breakdown
    };
  }, [data, period, year]);

  if (error) return <div style={styles.errorBox}>{error}</div>;
  if (!data) return null;
  if (!data.targets) {
    return (
      <div style={styles.card}>
        <h2 style={styles.h2}>🎯 Performance Targets · {year}</h2>
        <p style={{ color: colors.textMuted, margin: 0, fontSize: 13 }}>
          No targets set for {year} yet. Admin can set them under <em>Admin → Performance Targets</em>.
        </p>
      </div>
    );
  }

  const tiers = TIERS.map(t => {
    const annualTarget = Number(data.targets[t.key] || 0);
    const periodTarget = annualTarget / scope.denom;
    const pct = periodTarget > 0 ? scope.realized / periodTarget : 0;
    return { ...t, annualTarget, periodTarget, pct };
  });
  // Highest tier the rep is currently above pace for (for the headline)
  const tierHit = [...tiers].reverse().find(t => t.pct >= 1);
  const headline = tierHit
    ? `Tracking past ${tierHit.label}`
    : tiers[0].pct > 0
      ? `Tracking toward ${tiers.find(t => t.pct < 1)?.label || tiers[0].label}`
      : 'No realized $ yet this period';

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>
          🎯 Performance Targets
          <HelpGlyph text={HELP.widget} />
          <span style={{ color: colors.textMuted, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>· {scope.periodLabel}</span>
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          {/* Row 1: Annual + Q1-Q4. Current quarter tinted orange. */}
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
            {(() => {
              const opts = [{ v: 'annual', label: 'Annual' }];
              for (let q = 1; q <= 4; q++) {
                opts.push({ v: `q${q}`, label: `Q${q}`, isCurrent: q === data.current_quarter });
              }
              return opts.map(opt => {
                const active = period === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setPeriod(opt.v)}
                    title={opt.isCurrent ? 'Current quarter' : undefined}
                    style={{
                      padding: '6px 14px', border: 'none', cursor: 'pointer',
                      fontSize: 12, fontWeight: 600,
                      background: active ? colors.charcoal : '#fff',
                      color: active ? '#fff' : (opt.isCurrent ? colors.orange : colors.charcoal)
                    }}
                  >{opt.label}</button>
                );
              });
            })()}
          </div>
          {/* Row 2: Jan-Dec. Current month tinted orange. */}
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
            {(['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']).map((label, idx) => {
              const m = idx + 1;
              const v = `m${m}`;
              const active = period === v;
              const isCurrent = m === data.current_month;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setPeriod(v)}
                  title={isCurrent ? 'Current month' : undefined}
                  style={{
                    padding: '6px 9px', border: 'none', cursor: 'pointer',
                    fontSize: 11, fontWeight: 600,
                    background: active ? colors.charcoal : '#fff',
                    color: active ? '#fff' : (isCurrent ? colors.orange : colors.charcoal)
                  }}
                >{label}</button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 12 }} title={HELP.realized}>
        Realized <strong style={{ color: colors.charcoal, fontSize: 16 }}>{fmtMoneyK(scope.realized)}</strong>
        {' '}billed
        <HelpGlyph text={HELP.realized} />
        {' · '}{headline}
      </div>

      {/* Tier ladder rows -- whole row gets the tier tooltip so hovering
          anywhere on the bar explains how progress is calculated. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} title={HELP.tier}>
        {tiers.map(t => {
          const hit = t.pct >= 1;
          const widthPct = Math.min(100, Math.round(t.pct * 100));
          const barColor = hit ? colors.green
                        : t.pct >= 0.7 ? colors.orange
                        : t.pct >= 0.4 ? colors.amber
                        : colors.gray;
          // Phone: the 140px-label 4-column grid left the progress bar a few
          // pixels wide, so the label + numbers go on one line and the bar
          // gets the full width beneath. Desktop keeps the single-row grid.
          if (isMobile) {
            return (
              <div key={t.key} style={{ fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontWeight: 600, color: hit ? colors.green : colors.text }}>
                    {hit ? '✓ ' : ''}{t.label}
                  </span>
                  <span style={{ color: colors.textMuted, whiteSpace: 'nowrap' }}>
                    {fmtMoneyK(t.periodTarget)} · <strong style={{ color: hit ? colors.green : colors.textMuted }}>{fmtPct(t.pct)}</strong>
                  </span>
                </div>
                <div style={{ height: 10, background: colors.graySoft, borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${widthPct}%`, height: '100%', background: barColor, transition: 'width 0.4s' }} />
                </div>
              </div>
            );
          }
          return (
            <div key={t.key} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 90px 60px', alignItems: 'center', gap: 10, fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: hit ? colors.green : colors.text }}>
                {hit ? '✓ ' : ''}{t.label}
              </div>
              <div style={{ height: 10, background: colors.graySoft, borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ width: `${widthPct}%`, height: '100%', background: barColor, transition: 'width 0.4s' }} />
              </div>
              <div style={{ textAlign: 'right', color: colors.textMuted }}>
                {fmtMoneyK(t.periodTarget)}
              </div>
              <div style={{ textAlign: 'right', fontWeight: 600, color: hit ? colors.green : colors.textMuted }}>
                {fmtPct(t.pct)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Commission section -- hidden by default. Admin opts in per user
          via Admin -> Users -> "Show commission rates" checkbox.
          Until the RentalMan invoice feed has loaded we show a placeholder;
          after that, real Earned / Owed numbers. Earned = commission $ on
          PAID invoices; Owed = Earned - already-paid-out
          (commission_payments ledger). */}
      {data.show_commission_rates && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 12 }} title={HELP.commission}>
          <div style={{ color: colors.textMuted }}>
            <strong style={{ color: colors.charcoal }}>Your rates:</strong> {data.rates.rentals_pct}% rentals · {data.rates.labor_pct}% labor · {data.rates.consumables_pct}% consumables
            <HelpGlyph text={HELP.commission} />
          </div>
          {comm && comm.has_paid_data ? (
            <div style={{ color: colors.textMuted, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span title="Commission earned on PAID invoices YTD (status PD in the RentalMan invoice report).">
                <strong style={{ color: colors.charcoal }}>Earned:</strong>{' '}
                <strong style={{ color: colors.green }}>{fmtMoneyK(comm.earned)}</strong>
              </span>
              <span title="Commission on billed-but-not-yet-paid invoices (status OP). Will become Earned when the customer pays.">
                <strong style={{ color: colors.charcoal }}>Outstanding:</strong>{' '}
                <strong style={{ color: '#92400E' }}>{fmtMoneyK(comm.outstanding)}</strong>
              </span>
              <span title="Commission earned − commission already paid out to you. What you're still owed.">
                <strong style={{ color: colors.charcoal }}>Owed:</strong>{' '}
                <strong style={{ color: comm.owed > 0 ? '#991B1B' : colors.green }}>{fmtMoneyK(comm.owed)}</strong>
              </span>
            </div>
          ) : (
            <div style={{
              color: '#92400E', background: '#FEF3C7', border: '1px solid #FBBF24',
              padding: '4px 10px', borderRadius: 6, fontWeight: 600
            }}>
              ⏳ Earned commission · waiting on the RentalMan invoice report
            </div>
          )}
        </div>
      )}
    </div>
  );
}
